/**
 * Maps ISO 3166-1 alpha-2 country codes to the SVG filenames under
 * `public/media/flags/`. Not every country in the flag set is listed here —
 * this is the short list of partners that actually show up in German trade
 * register and LEI parent/subsidiary data. Unknown codes resolve to null and
 * the caller is expected to fall back to a neutral icon.
 */

const COUNTRY_CODE_TO_FLAG: Record<string, string> = {
  AD: 'andorra',
  AE: 'united-arab-emirates',
  AF: 'afghanistan',
  AL: 'albania',
  AM: 'armenia',
  AR: 'argentina',
  AT: 'austria',
  AU: 'australia',
  AZ: 'azerbaijan',
  BA: 'bosnia-and-herzegovina',
  BB: 'barbados',
  BD: 'bangladesh',
  BE: 'belgium',
  BG: 'bulgaria',
  BH: 'bahrain',
  BM: 'bermuda',
  BN: 'brunei',
  BO: 'bolivia',
  BR: 'brazil',
  BS: 'bahamas',
  BY: 'belarus',
  BZ: 'belize',
  CA: 'canada',
  CH: 'switzerland',
  CI: 'ivory-coast',
  CL: 'chile',
  CN: 'china',
  CO: 'colombia',
  CR: 'costa-rica',
  CU: 'cuba',
  CW: 'curacao',
  CY: 'cyprus',
  CZ: 'czech-republic',
  DE: 'germany',
  DK: 'denmark',
  DO: 'dominican-republic',
  DZ: 'algeria',
  EC: 'ecuador',
  EE: 'estonia',
  EG: 'egypt',
  ES: 'spain',
  ET: 'ethiopia',
  FI: 'finland',
  FJ: 'fiji',
  FR: 'france',
  GB: 'united-kingdom',
  GE: 'georgia',
  GH: 'ghana',
  GI: 'gibraltar',
  GR: 'greece',
  GT: 'guatemala',
  HK: 'hong-kong',
  HN: 'honduras',
  HR: 'croatia',
  HU: 'hungary',
  ID: 'indonesia',
  IE: 'ireland',
  IL: 'israel',
  IN: 'india',
  IQ: 'iraq',
  IR: 'iran',
  IS: 'iceland',
  IT: 'italy',
  JM: 'jamaica',
  JO: 'jordan',
  JP: 'japan',
  KE: 'kenya',
  KG: 'kyrgyzstan',
  KH: 'cambodia',
  KR: 'south-korea',
  KP: 'north-korea',
  KW: 'kuwait',
  KY: 'cayman-islands',
  KZ: 'kazakhstan',
  LA: 'laos',
  LB: 'lebanon',
  LI: 'liechtenstein',
  LK: 'sri-lanka',
  LT: 'lithuania',
  LU: 'luxembourg',
  LV: 'latvia',
  LY: 'libya',
  MA: 'morocco',
  MC: 'monaco',
  MD: 'moldova',
  ME: 'montenegro',
  MK: 'republic-of-macedonia',
  MN: 'mongolia',
  MT: 'malta',
  MU: 'mauritius',
  MV: 'maldives',
  MX: 'mexico',
  MY: 'malaysia',
  NG: 'nigeria',
  NI: 'nicaragua',
  NL: 'netherlands',
  NO: 'norway',
  NP: 'nepal',
  NZ: 'new-zealand',
  OM: 'oman',
  PA: 'panama',
  PE: 'peru',
  PH: 'philippines',
  PK: 'pakistan',
  PL: 'poland',
  PR: 'puerto-rico',
  PT: 'portugal',
  PY: 'paraguay',
  QA: 'qatar',
  RO: 'romania',
  RS: 'serbia',
  RU: 'russia',
  SA: 'saudi-arabia',
  SE: 'sweden',
  SG: 'singapore',
  SI: 'slovenia',
  SK: 'slovakia',
  SM: 'san-marino',
  SV: 'el-salvador',
  SY: 'syria',
  TH: 'thailand',
  TN: 'tunisia',
  TR: 'turkey',
  TW: 'taiwan',
  UA: 'ukraine',
  UK: 'united-kingdom',
  US: 'united-states',
  UY: 'uruguay',
  UZ: 'uzbekistan',
  VA: 'vatican-city',
  VE: 'venezuela',
  VN: 'vietnam',
  VU: 'vanuatu',
  YE: 'yemen',
  ZA: 'south-africa',
  ZM: 'zambia',
  ZW: 'zimbabwe',
};

/**
 * Returns the public URL for a country's flag SVG, or null when the country
 * code is unknown. The caller should fall back to a neutral icon in that case.
 */
export function flagUrlForCountry(code?: string | null): string | null {
  if (!code) return null;
  const slug = COUNTRY_CODE_TO_FLAG[code.trim().toUpperCase()];
  return slug ? `/media/flags/${slug}.svg` : null;
}
